import React from 'react';
import ProgressBar from '../../components/ProgressBar/ProgressBar';
import FadeInSection from '../../components/FadeInSection';
import SkeletonPage from '../../components/SkeletonPage/SkeletonPage';
import accessModelImg from '../../assets/images/safe__access-model.png';
import coverImg from '../../assets/images/safe__cover.png';
import disclosureLogicImg from '../../assets/images/safe__disclosure-logic.png';
import edgeCasesImg from '../../assets/images/safe__edge-cases.png';
import flow1Img from '../../assets/images/safe__flow-1.png';
import flow2Img from '../../assets/images/safe__flow-2.png';
import flow3Img from '../../assets/images/safe__flow-3.png';
import flow4Img from '../../assets/images/safe__flow-4.png';
import keyDecisionsImg from '../../assets/images/safe__key-decisions.png';
import './Safe.scss';

const Safe = () => {
  const [isImageLoaded, setIsImageLoaded] = React.useState(false);

  React.useEffect(() => {
    const img = new Image();
    img.src = coverImg;

    img.onload = () => {
      setIsImageLoaded(true);
    };

    img.onerror = () => {
      console.error('Ошибка загрузки изображения');
      setIsImageLoaded(true);
    };
  }, []);

  return (
    <>
      {!isImageLoaded && <SkeletonPage />}
      {isImageLoaded && (
        <section className="safe case">
          <ProgressBar />
          <FadeInSection>
            <h1 className="case__title">
              Safe impersonation for a multi-tenant security platform.
              <br />A feature I designed for letting one person act inside another user's account
              on a product whose whole job is security,{' '}
              <span className="case__title-span">without breaking that security</span>
            </h1>
          </FadeInSection>
          <div className="case__content">
            <FadeInSection>
              <div className="safe__cover">
                <img src={coverImg} alt="Safe impersonation user selector" />
              </div>
            </FadeInSection>
            <div className="safe__overview">
              <FadeInSection>
                <h2>
                  <span className="case__title-span">Give the least access the job needs.</span>{' '}
                  Make it impossible to forget. Log all of it
                </h2>
              </FadeInSection>
              <div className="text-and-image__paragraph">
                <FadeInSection>
                  <h3 className="text-and-image__title">Context</h3>
                  <div>
                    <p>
                      A multi-tenant security platform. Operators manage many client organizations
                      and their users. Two roles regularly need to act as a specific user:
                      <br />— <span>Support and admins</span> — to reproduce and fix a user's
                      problem.
                      <br />— <span>External installers</span> — to set up a user's environment
                      before handing it over
                      <br />
                      <br />
                      Both need to see and do things as that user. On a security product, that is
                      the problem, not a convenience
                    </p>
                  </div>
                </FadeInSection>
                <FadeInSection>
                  <h3 className="text-and-image__title">The problem</h3>
                  <div>
                    <p>
                      Before a real feature, &quot;helping a user&quot; meant one of three bad
                      options:
                      <br />
                      <br />— Ask the user to screen-share or read out their screen — slow, and you
                      can't reproduce the bug.
                      <br />— Share credentials — no audit, no expiry, far too much access.
                      <br />— Grant standing broad access — same, plus a privacy hole
                      <br />
                      <br />
                      Every one of these breaks the rules the product sells. So the question was
                      narrow: let someone act as a user, and keep it safe
                    </p>
                  </div>
                </FadeInSection>
              </div>
            </div>
            <div className="safe__access-model">
              <div className="text-and-image__paragraph safe__access-row">
                <FadeInSection>
                  <h3 className="text-and-image__title">The access model</h3>
                  <div>
                    <p>
                      The design turns on who is doing the impersonating. An internal actor is
                      trusted; an external one crosses a boundary
                      <br />
                      <br />
                      The more external the actor, the more the user stays in control
                    </p>
                  </div>
                </FadeInSection>
              </div>
              <FadeInSection>
                <img
                  className="safe__access-model-image"
                  src={accessModelImg}
                  alt="Comparison of internal and external impersonation access"
                />
              </FadeInSection>
              <div className="text-and-image__paragraph safe__access-row">
                <FadeInSection>
                  <h3
                    className="text-and-image__title safe__access-heading-placeholder"
                    aria-hidden="true"
                  >
                    The access model
                  </h3>
                  <div>
                    <p>
                      For external actors, &quot;visibility after&quot; is literal: the session also
                      appears in the end user's own activity log — the same trail, framed in their
                      words — so whoever consented can see exactly what was done
                      <br />
                      <br />
                      When the session ends, they also get a summary email — what was accessed and
                      which categories were revealed — so the flow that opened with a consent code
                      closes with a receipt. Internal sessions stay in the company log only
                    </p>
                  </div>
                </FadeInSection>
              </div>
            </div>
            <div className="safe__how-it-works">
              <FadeInSection>
                <h2>How it works</h2>
              </FadeInSection>
              <div className="safe__flow-step">
                <div className="text-and-image__paragraph safe__flow-row">
                  <FadeInSection>
                    <h3 className="text-and-image__title">#1</h3>
                    <div>
                      <p>
                        Impersonation starts from where you already are — the users list
                      </p>
                    </div>
                  </FadeInSection>
                </div>
                <FadeInSection>
                  <img
                    className="safe__flow-image"
                    src={flow1Img}
                    alt="Users list with impersonate user action"
                  />
                </FadeInSection>
              </div>
              <div className="safe__flow-step">
                <div className="text-and-image__paragraph safe__flow-row">
                  <FadeInSection>
                    <h3 className="text-and-image__title">#2</h3>
                    <div>
                      <p>
                        A colored frame and a persistent bar make &quot;who am I right now&quot;
                        impossible to miss. Sensitive data stays masked until revealed
                      </p>
                    </div>
                  </FadeInSection>
                </div>
                <FadeInSection>
                  <img
                    className="safe__flow-image"
                    src={flow2Img}
                    alt="Active impersonation frame and persistent status bar"
                  />
                </FadeInSection>
              </div>
              <div className="safe__flow-step">
                <div className="text-and-image__paragraph safe__flow-row">
                  <FadeInSection>
                    <h3 className="text-and-image__title">#3</h3>
                    <div>
                      <p>
                        External actors only. The email code is both consent and proof of identity
                      </p>
                    </div>
                  </FadeInSection>
                </div>
                <FadeInSection>
                  <img
                    className="safe__flow-image"
                    src={flow3Img}
                    alt="Email with external impersonation confirmation code"
                  />
                </FadeInSection>
              </div>
              <div className="safe__flow-step">
                <div className="text-and-image__paragraph safe__flow-row">
                  <FadeInSection>
                    <h3 className="text-and-image__title">#4</h3>
                    <div>
                      <p>
                        Every session is accountable. Internal sessions live in the company log
                        only; external sessions also appear in the user's own log
                      </p>
                    </div>
                  </FadeInSection>
                </div>
                <FadeInSection>
                  <img
                    className="safe__flow-image"
                    src={flow4Img}
                    alt="Impersonation activity log"
                  />
                </FadeInSection>
                <div className="text-and-image__paragraph safe__flow-row">
                  <FadeInSection>
                    <h3
                      className="text-and-image__title safe__flow-heading-placeholder"
                      aria-hidden="true"
                    >
                      #4
                    </h3>
                    <div>
                      <p>
                        The log is a focused view of the wider activity log — impersonation gets
                        its own lens because it's high-risk. Any row opens a session panel with the
                        full timeline, and each session downloads as a tamper-evident JSON record:
                        every reveal is logged by category, never by value. Failed sessions appear
                        too — an expired external code, for example — so the consent gate is visible
                        doing its job
                      </p>
                    </div>
                  </FadeInSection>
                </div>
              </div>
            </div>
            <div className="safe__decision-section">
              <div className="text-and-image__paragraph">
                <FadeInSection>
                  <h3 className="text-and-image__title">Key decisions</h3>
                  <div>
                    <p>
                      In the process of my work, I designed several screen options to determine
                      the most successful one
                      <br />
                      <br />I tested the sequence of steps with my respondent. No downsides were
                      found in the flow. Once again, I emphasize that data from one respondent
                      would be subjective and not representative of a real product
                    </p>
                  </div>
                </FadeInSection>
              </div>
              <FadeInSection>
                <img
                  className="safe__decision-image"
                  src={keyDecisionsImg}
                  alt="Four key impersonation design decisions and rejected alternatives"
                />
              </FadeInSection>
            </div>
            <div className="safe__decision-section">
              <div className="text-and-image__paragraph">
                <FadeInSection>
                  <h3 className="text-and-image__title">The logic in one picture</h3>
                  <div>
                    <p>
                      Consent before, visibility after — both scale with distance from the company
                    </p>
                  </div>
                </FadeInSection>
              </div>
              <FadeInSection>
                <img
                  className="safe__decision-image"
                  src={disclosureLogicImg}
                  alt="Data disclosure rules during impersonation"
                />
              </FadeInSection>
            </div>
            <FadeInSection>
              <h2 className="safe__disclosure-statement">
                The level of disclosure during impersonation depends on the data's sensitivity,{' '}
                <span className="case__title-span">to ensure user safety</span>
              </h2>
            </FadeInSection>
            <div className="safe__decision-section">
              <div className="text-and-image__paragraph">
                <FadeInSection>
                  <h3 className="text-and-image__title">Edge cases and error states</h3>
                  <div>
                    <p>
                      The interesting part of impersonation isn't the happy path — it's what
                      happens when a session doesn't end cleanly
                    </p>
                  </div>
                </FadeInSection>
              </div>
              <FadeInSection>
                <img
                  className="safe__decision-image"
                  src={edgeCasesImg}
                  alt="Impersonation edge cases and system error responses"
                />
              </FadeInSection>
            </div>
            <div className="text-and-image__paragraph safe__outcome">
              <FadeInSection>
                <h3 className="text-and-image__title">Outcome</h3>
                <div>
                  <p>
                    Support can help <span>without breaking least-privilege</span>
                    <br />
                    <br />
                    <span>Privacy is protected</span> by default, opened only on purpose
                    <br />
                    <br />
                    Outside access is consented and <span>fully traceable</span>
                    <br />
                    <br />
                    Every <span>session is accountable</span>
                  </p>
                </div>
              </FadeInSection>
            </div>
            <FadeInSection>
              <div className="safe__reflection">
                <p>
                  <span>The hardest part wasn't the interface — it was deciding</span> how much to
                  trust internal actors
                </p>
                <p>
                  I chose org-level accountability over per-user approval: trusted employees,
                  every action logged
                </p>
              </div>
            </FadeInSection>
          </div>
        </section>
      )}
    </>
  );
};

export default Safe;
